import React, { useEffect, useState } from "react";
import CustomAlert from "../../helpers/CustomAlert";
import { format, parse } from "date-fns";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import { useSelector } from "react-redux";
import { formatDateMonth, formatDateToYYYYMMDD } from "../../helpers/DateHelpers";
import MasterTable from "../../components/common/MasterTable";
import Loader from "../../components/Loader/Loader";
import HeaderWithBreadcrumb from "../../components/common/HeaderWithBreadcrumb";
import LedgerReportFilterModal from "./LedgerReportFilterModal";
import { formatAmount, formatDate } from "../../helpers/helper";
import useExcelDownloader from "../../Hooks/useExcelDownloader";
import useTableExport from "../../Hooks/useTableExport";
import PaymentStatusModal from "../Payment/components/PaymentStatusModal";
import PaymentReceipt from "../Payment/components/PaymentReceipt";
import FilterBar from "../../components/common/FilterBar";
import { DR_CR_OPTIONS } from "../../components/common/filterOptions";

const PAYMENT_MODE_OPTIONS = [
  { value: "WALLET", label: "Wallet" },
  { value: "UPI", label: "UPI" },
  { value: "DCI", label: "Debit Card (DCI)" },
  { value: "CCI", label: "Credit Card (CCI)" },
];

// Helper function to get date 7 days ago in YYYY-MM-DD format
const getLast7DaysDate = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const year = sevenDaysAgo.getFullYear();
  const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
  const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultFilterData = {
  walletNumber: "",
  fromDate: getLast7DaysDate(),
  toDate: getTodayDate(),
  fromAmount: "",
  toAmount: "",
  drCr: "",
  batchNumber: "",
  status: "",
  createdBy: "",
  walletUserName: "",
  paymentMode: "",
  ewaybillNumber: "",
  search: "",
};

const LedgerReport = () => {
  const pageSize = 10;
  const { user } = useSelector((state) => state.auth);
  const { downloadExcel } = useExcelDownloader();
  const { exportAllData, exportLoading } = useTableExport(downloadExcel);
  const dashboardData = useSelector((state) => state.balance.balance);

  const [noData, setNoData] = useState(false);
  const [ledgerReportData, setLedgerReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageDetails, setPageDetails] = useState(null);
  const loggedInUserName = sessionStorage.getItem("username") || "admin";
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [columnData, setColumnData] = useState("");
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const handleReset = () => {
    const resetFilterData = {
      walletNumber: "",
      fromDate: getLast7DaysDate(),
      toDate: getTodayDate(),
      fromAmount: "",
      toAmount: "",
      drCr: "",
      batchNumber: "",
      status: "",
      createdBy: "",
      walletUserName: "",
      paymentMode: "",
      ewaybillNumber: "",
    };
    setFilterData(resetFilterData);
    setCurrentPage(1);
    fetchLedgerReportData(1, resetFilterData);
  };

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const getLedgerReportHeaders = () => {
    const allHeaders = [
      "Wallet Name",
      "Wallet Number",
      "Txn Date & Time",
      "Opening Bal (₹)",
      "Amount (₹)",
      "Dr/Cr",
      "Closing Bal (₹)",
      "Payment Mode",
      "Ewaybill Number",
      "Narration",
      "Batch No",
      "Seq No",
    ];

    if (user?.data?.userProfile === "CUSTOMER") {
      const columnsToHide = ["Wallet Name", "Wallet Number", "Seq No"];
      return allHeaders.filter((header) => !columnsToHide.includes(header));
    }

    return allHeaders;
  };

  const getLedgerReportHeaderToKeyMap = () => {
    const allMappings = {
      "Wallet Name": "walletUserName",
      "Wallet Number": "walletNumber",
      "Txn Date & Time": "transactionDateTime",
      "Opening Bal (₹)": "openingBalance",
      "Amount (₹)": "amount",
      "Dr/Cr": "drCr",
      "Closing Bal (₹)": "closingBalance",
      "Payment Mode": "paymentMode",
      "Ewaybill Number": "ewaybillNumber",
      "Narration": "narration",
      // "Key Value": "ewaybillNumber",
      "Batch No": "batchNumber",
      "Seq No": "sequenceNumber",
    };

    if (user?.data?.userProfile === "CUSTOMER") {
      const {
        "Wallet Name": c0,
        "Wallet Number": c1,
        "Seq No": c2,
        ...filteredMappings
      } = allMappings;
      return filteredMappings;
    }

    return allMappings;
  };

  useEffect(() => {
    fetchLedgerReportData(1, defaultFilterData);
  }, []);

  const fetchLedgerReportData = async (page, filterData) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        ...filterData,
      };

      if (filterData?.walletNumber?.trim()) {
        requestBody.walletNumber = filterData.walletNumber.trim();
      }

      if (filterData?.fromDate) {
        requestBody.fromDate = formatDate(filterData.fromDate);
      }

      if (filterData?.toDate) {
        requestBody.toDate = formatDate(filterData.toDate);
      }

      if (filterData?.createdBy?.trim()) {
        requestBody.createdBy = filterData.createdBy.trim();
      }

      // Add drCr filter if selected (value should be "DR" or "CR")
      if (filterData?.drCr) {
        requestBody.drCr = filterData.drCr;
      }

      // Add paymentMode filter if selected (value should be "WALLET" or "UPI")
      if (filterData?.paymentMode) {
        requestBody.paymentMode = filterData.paymentMode;
      }

      const response = await axiosPostApi(
        `${apiList.FETCH_LEDGER_REPORT_DATA}?pageSize=${pageSize}&pageNo=${page}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails || null);
        setCurrentPage(page);

        const modifiedData = modifyStructure(response?.data?.data || []);

        setLedgerReportData(modifiedData);
        if (modifiedData?.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
        }
      } else {
        setNoData(true);
        setLedgerReportData([]);
        setPageDetails(null);

        if (response?.data?.respCode === "01") {
          // no matches: the empty table says so
        } else if (
          response?.data?.data === null ||
          response?.data?.data?.length === 0
        ) {
          CustomAlert({
            title: "No Data Found",
            text: "No matching records found for the specified criteria.",
            icon: "info",
            confirmButtonText: "Okay",
          });
        } else {
          CustomAlert({
            title: "Error",
            text:
              response?.data?.message ||
              "An error occurred while fetching data.",
            icon: "error",
            confirmButtonText: "Okay",
          });
        }
      }
    } catch (error) {
      console.error("API Error:", error);
      setNoData(true);
      setLedgerReportData([]);
      setPageDetails(null);

      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      }).then((result) => {
        if (result.isConfirmed) {
          fetchLedgerReportData(page, filterData);
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    const modified = response?.map((item, index) => {
      const txnDateTimeParsed = item?.transactionDateTime
        ? parse(item.transactionDateTime, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const createdDateParsed = item?.createdDate
        ? parse(item.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const modifiedDateParsed = item?.modifiedDate
        ? parse(item.modifiedDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const authDateParsed = item?.authDate
        ? parse(item.authDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const formattedAmount =
        item?.amount !== undefined ? Number(item.amount).toFixed(2) : "";

      const formattedNarration = item?.narration
        ? item.narration.length > 40
          ? `${item.narration.substring(0, 40)}...`
          : item.narration
        : "";

      return {
        ...item,

        transactionDateTime: txnDateTimeParsed
          ? [
              format(txnDateTimeParsed, "dd-MM-yyyy"),
              <br key={`txn-${index}`} />,
              format(txnDateTimeParsed, "HH:mm:ss"),
            ]
          : "",

        createdDate: createdDateParsed
          ? [
              format(createdDateParsed, "dd-MM-yyyy"),
              <br key={`created-${index}`} />,
              format(createdDateParsed, "HH:mm:ss"),
            ]
          : "",

        amount: (
          <div
            style={{
              width: "82px",
              textAlign: "end",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.amount)}
          >
            {formatAmount(item.amount)}
          </div>
        ),

        openingBalance: (
          <div
            style={{
              width: "111px",
              textAlign: "end",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.openingBalance)}
          >
            {formatAmount(item.openingBalance)}
          </div>
        ),

        closingBalance: (
          <div
            style={{
              textAlign: "end",
              width: "102px",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.closingBalance)}
          >
            {formatAmount(item.closingBalance)}
          </div>
        ),

        sequenceNumber: (
          <div
            style={{
              width: "51px",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
            title={item.sequenceNumber}
          >
            {item.sequenceNumber}
          </div>
        ),

        modifiedDate: modifiedDateParsed
          ? [
              format(modifiedDateParsed, "dd-MM-yyyy"),
              <br key={`modified-${index}`} />,
              format(modifiedDateParsed, "HH:mm:ss"),
            ]
          : "",

        authDate: authDateParsed
          ? [
              format(authDateParsed, "dd-MM-yyyy"),
              <br key={`auth-${index}`} />,
              format(authDateParsed, "HH:mm:ss"),
            ]
          : "",

        refund:
          item?.refund === true
            ? "True"
            : item?.refund === false
            ? "False"
            : "",

        // amount: formattedAmount,

        narration: item?.narration || "",

        walletNumber: item?.walletNumber || "",

        transactionDateTimeE: item?.transactionDateTime,
        amountE: item?.amount,
      };
    });

    return modified;
  };

  const handleView = (data) => {
    setColumnData(data);
  };

  const handleDownloadExcel = () => {
    const requestBody = {
      username: loggedInUserName,
      ...filterData,
    };

    if (filterData?.walletNumber?.trim()) {
      requestBody.walletNumber = filterData.walletNumber.trim();
    }

    if (filterData?.fromDate) {
      requestBody.fromDate = formatDate(filterData.fromDate);
    }

    if (filterData?.toDate) {
      requestBody.toDate = formatDate(filterData.toDate);
    }

    if (filterData?.createdBy?.trim()) {
      requestBody.createdBy = filterData.createdBy.trim();
    }

    if (filterData?.drCr) {
      requestBody.drCr = filterData.drCr;
    }

    if (filterData?.paymentMode) {
      requestBody.paymentMode = filterData.paymentMode;
    }

    // ✅ Get current date & time (NEW)
    const now = new Date();

    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    // DD_MM_YYYY_HH_MM_SS
    const currentDate = `${day}_${month}_${year}_${hours}_${minutes}_${seconds}`;

    // Format dates for filename
    const formatDateForFilename = (dateString) => {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-");
      return `${day}_${month}_${year}`;
    };

    // Default range helpers
    const getLast7DaysDate = () => {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      const year = sevenDaysAgo.getFullYear();
      const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
      const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const getTodayDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const isDefaultRange =
      filterData.fromDate === getLast7DaysDate() &&
      filterData.toDate === getTodayDate();

    let fileName;

    if (
      isDefaultRange &&
      !filterData.walletNumber?.trim() &&
      !filterData.batchNumber?.trim() &&
      !filterData.drCr?.trim() &&
      !filterData.paymentMode?.trim()
    ) {
      fileName = `Ledger_Report_${currentDate}_Last7Days.xlsx`;
    } else {
      const parts = [`Ledger_Report_${currentDate}`];

      // Date range
      if (filterData.fromDate && filterData.toDate) {
        const fromDateFormatted = formatDateForFilename(filterData.fromDate);
        const toDateFormatted = formatDateForFilename(filterData.toDate);
        parts.push(`${fromDateFormatted}_to_${toDateFormatted}`);
      }

      // Wallet number
      if (filterData.walletNumber?.trim()) {
        parts.push(`Wallet_${filterData.walletNumber.trim()}`);
      }

      // Wallet name
      if (filterData.walletUserName?.trim()) {
        parts.push(`Name_${filterData.walletUserName.trim()}`);
      }

      // Batch number
      if (filterData.batchNumber?.trim()) {
        parts.push(`Batch_${filterData.batchNumber.trim()}`);
      }

      // Dr/Cr
      if (filterData.drCr?.trim()) {
        parts.push(filterData.drCr.trim());
      }

      // Payment mode
      if (filterData.paymentMode?.trim()) {
        parts.push(
          filterData.paymentMode.trim() === "WALLET" ? "Wallet" : "UPI",
        );
      }

      // Created by
      if (filterData.createdBy?.trim()) {
        parts.push(`CreatedBy_${filterData.createdBy.trim()}`);
      }

      fileName = parts.join("_") + ".xlsx";
    }

    // Sanitize filename
    fileName = fileName.replace(/[<>:"/\\|?*]/g, "");

    // Limit length
    if (fileName.length > 200) {
      fileName = `Ledger_Report_${currentDate}_With_Filters.xlsx`;
    }

    exportAllData({
      apiUrl: apiList.FETCH_LEDGER_REPORT_DATA,
      requestBody,
      headers: getLedgerReportHeaders(),
      headerToKeyMap: getLedgerReportHeaderToKeyMap(),
      fileName: fileName,
    });
  };

  const handleAction = (data, actionType = "view") => {
    setColumnData(data);
    if(actionType === "downloadReceipt"){
      setShowStatusModal(true);
    }
  }

  const pageSizeCount = pageDetails === null ? 0 : pageDetails.pageSize;
  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <>
      <HeaderWithBreadcrumb title="Ledger Report" breadcrumb="SMS" />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          fetchLedgerReportData(1, next);
        }}
        search={{ key: "search", placeholder: "Search wallet no., user, narration, batch no. or e-way bill" }}
        selects={[
          { key: "drCr", allLabel: "Debit and credit", chipLabel: "Type", options: DR_CR_OPTIONS },
          { key: "paymentMode", allLabel: "All payment modes", chipLabel: "Mode", options: PAYMENT_MODE_OPTIONS },
        ]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Date" }}
      />
      <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
        {loading ? (
          <Loader />
        ) : (
          <>
            <MasterTable
              loading={loading}
              headers={getLedgerReportHeaders()}
              data={ledgerReportData}
              filterFields={["walletNumber", "batchNumber"]}
              handleAction={handleAction}
              entityType="LedgerReport"
              headerToKeyMap={getLedgerReportHeaderToKeyMap()}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              pageSize={pageSizeCount}
              totalRecord={totalRecord}
              fetchNextData={(page) => fetchLedgerReportData(page, filterData)}
              setCurrentPage={setCurrentPage}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              fetchData={() => fetchLedgerReportData(1, defaultFilterData)}
              headerInfo={{
                "Wallet Name": dashboardData?.ledger?.userName,
                "Wallet No": dashboardData?.ledger?.walletNumber,
              }}
              handleExport={handleDownloadExcel}
              exportLoading={exportLoading}
              hideFilterControls
            />
          </>
        )}

        {isModalOpenFilter && (
          <LedgerReportFilterModal
            show={isModalOpenFilter}
            handleClose={closeModalFilter}
            setLedgerReportData={setLedgerReportData}
            setPageDetails={setPageDetails}
            setNoData={setNoData}
            currentPage={currentPage}
            pageSize={pageSize}
            filterData={filterData}
            setFilterData={setFilterData}
            handleReset={handleReset}
            setCurrentPage={setCurrentPage}
          />
        )}

        {/* {showStatusModal && 
          <PaymentStatusModal
            status={columnData?.status}
            amount={columnData?.amountE || 0}
            txnId={columnData?.transactionId || "N/A"}
            orderId={columnData?.referenceId || "N/A"}
            date={formatDateMonth(columnData?.transactionDateTimeE)}
            paymentMethod={columnData?.paymentMode || "N/A"}
            onClose={() => setShowStatusModal(false)}
          />
          } */}

        {showStatusModal && (
            <PaymentReceipt
              handleClose={() => setShowStatusModal(false)}
              data={columnData}
            />
        )}

      </div>
    </>
  );
};

export default LedgerReport;
